/**
 * Onglet Devis client — affiche tous les ContractQuote du contrat,
 * avec actions Accepter / Refuser sur les devis status="sent".
 *
 * PM Spec 06/05/2026 — Phase 1 G1
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { FileText, Loader2, CheckCircle, XCircle, Eye, Download as DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  draft: 'bg-slate-50 text-slate-600 border-slate-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  refused: 'bg-rose-50 text-rose-700 border-rose-200',
  expired: 'bg-amber-50 text-amber-700 border-amber-200',
};
const STATUS_LABEL = {
  draft: 'Brouillon', sent: 'Envoyé', accepted: 'Accepté',
  refused: 'Refusé', expired: 'Expiré',
};

const formatPrice = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' XOF';
const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

const ClientContractQuotesTab = ({ contractId }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionQuote, setActionQuote] = useState(null);
  const [actionType, setActionType] = useState(null); // 'accept' | 'refuse'
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchQuotes = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contracts/${contractId}/quotes`);
      setQuotes(r.data || []);
    } catch (err) {
      console.error('Error fetching quotes:', err);
    } finally { setLoading(false); }
  }, [contractId]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const openAction = (quote, type) => {
    setActionQuote(quote);
    setActionType(type);
    setReason('');
  };

  const submitAction = async () => {
    if (!actionQuote || !actionType) return;
    if (actionType === 'refuse' && reason.trim().length < 5) {
      toast.error('Merci de préciser un motif (min 5 caractères)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/client/contracts/${contractId}/quotes/${actionQuote.id}/${actionType}`,
        actionType === 'refuse' ? { reason: reason.trim() } : {}
      );
      toast.success(actionType === 'accept' ? 'Devis accepté ✓' : 'Devis refusé');
      setActionQuote(null);
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const downloadPDF = async (quote) => {
    try {
      const r = await axios.get(
        `${API}/contracts/${contractId}/quotes/${quote.id}/pdf`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quote.quoteNumber || quote.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Téléchargement impossible');
    }
  };

  return (
    <Card data-testid="client-contract-quotes-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="w-5 h-5 text-gray-400" />
          Devis ({quotes.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucun devis pour ce contrat</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° Devis</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Montant TTC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.map(q => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-medium font-mono text-xs">
                    {q.quoteNumber || `Q-${q.id?.slice(0, 8)}`}
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(q.issuedAt || q.createdAt)}</TableCell>
                  <TableCell className="text-sm">
                    {q.validityStart ? `${formatDate(q.validityStart)} → ${formatDate(q.validityEnd)}` : '—'}
                  </TableCell>
                  <TableCell className="font-medium">{formatPrice(q.totals?.totalTTC || q.totalTTC)}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] border ${STATUS_BADGE[q.status] || STATUS_BADGE.draft}`}>
                      {STATUS_LABEL[q.status] || q.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => downloadPDF(q)}
                            data-testid={`download-quote-${q.id}`}>
                      <DownloadIcon className="w-4 h-4" />
                    </Button>
                    {q.status === 'sent' && (
                      <>
                        <Button size="sm" variant="ghost"
                                className="text-emerald-700"
                                onClick={() => openAction(q, 'accept')}
                                data-testid={`accept-quote-${q.id}`}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Accepter
                        </Button>
                        <Button size="sm" variant="ghost"
                                className="text-rose-700"
                                onClick={() => openAction(q, 'refuse')}
                                data-testid={`refuse-quote-${q.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Refuser
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!actionQuote} onOpenChange={(o) => !o && setActionQuote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'accept' ? 'Accepter le devis' : 'Refuser le devis'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {actionQuote && (
              <div className="text-sm text-slate-600">
                <p><strong>{actionQuote.quoteNumber}</strong> — {formatPrice(actionQuote.totals?.totalTTC || actionQuote.totalTTC)}</p>
              </div>
            )}
            {actionType === 'refuse' && (
              <Textarea
                placeholder="Motif du refus (obligatoire)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                data-testid="refuse-reason-input"
              />
            )}
            {actionType === 'accept' && (
              <p className="text-sm text-slate-600">
                En acceptant ce devis, vous validez les conditions financières et opérationnelles.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionQuote(null)} disabled={submitting}>
              Annuler
            </Button>
            <Button
              onClick={submitAction}
              disabled={submitting}
              className={actionType === 'accept' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
              data-testid="confirm-action-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {actionType === 'accept' ? 'Accepter le devis' : 'Confirmer le refus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ClientContractQuotesTab;
